import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AIInsight, AIUsageStats } from '../../types';
import TokenUsage from './TokenUsage';

interface AIInsightsProps {
  openaiApiKey: string;
  aiInsights: string | null;
  aiLoading: boolean;
  currentInsight: AIInsight | null;
  usageStats: AIUsageStats;
  onAnalyze: () => void;
  onCloseInsights: () => void;
  onShowApiKeyModal: () => void;
  onClearAllData: () => void;
}

const AIInsights: React.FC<AIInsightsProps> = ({
  openaiApiKey,
  aiInsights,
  aiLoading,
  currentInsight,
  usageStats,
  onAnalyze,
  onCloseInsights,
  onShowApiKeyModal,
  onClearAllData,
}) => {
  const markdownComponents = {
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={tomorrow}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    // Custom styling for markdown elements
    h1: ({ children }: any) => <h1 className="markdown-h1">{children}</h1>,
    h2: ({ children }: any) => <h2 className="markdown-h2">{children}</h2>,
    h3: ({ children }: any) => <h3 className="markdown-h3">{children}</h3>,
    h4: ({ children }: any) => <h4 className="markdown-h4">{children}</h4>,
    ul: ({ children }: any) => <ul className="markdown-ul">{children}</ul>,
    ol: ({ children }: any) => <ol className="markdown-ol">{children}</ol>,
    li: ({ children }: any) => <li className="markdown-li">{children}</li>,
    p: ({ children }: any) => <p className="markdown-p">{children}</p>,
    blockquote: ({ children }: any) => <blockquote className="markdown-blockquote">{children}</blockquote>,
    strong: ({ children }: any) => <strong className="markdown-strong">{children}</strong>,
    em: ({ children }: any) => <em className="markdown-em">{children}</em>,
    table: ({ children }: any) => <table className="markdown-table">{children}</table>,
    thead: ({ children }: any) => <thead className="markdown-thead">{children}</thead>,
    tbody: ({ children }: any) => <tbody className="markdown-tbody">{children}</tbody>,
    tr: ({ children }: any) => <tr className="markdown-tr">{children}</tr>,
    th: ({ children }: any) => <th className="markdown-th">{children}</th>,
    td: ({ children }: any) => <td className="markdown-td">{children}</td>,
  };

  return (
    <div className="ai-insights-section">
      <div className="ai-header">
        <h2>🤖 AI Task Analysis</h2>
        <button
          onClick={onAnalyze}
          disabled={aiLoading}
          className="ai-analyze-button"
        >
          {aiLoading ? '🔄 Analyzing...' : '✨ Analyze My Tasks'}
        </button>
      </div>
      
      {aiInsights && (
        <div className="ai-insights-content">
          <div className="ai-insights-header">
            <h3>Analysis Results</h3>
            <button
              onClick={onCloseInsights}
              className="close-insights-button"
            >
              ✕ Close
            </button>
          </div>
          
          <div className="ai-insights-text markdown-content">
            <ReactMarkdown 
              components={markdownComponents}
              remarkPlugins={[remarkGfm]}
            >
              {aiInsights}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {(currentInsight || usageStats.total_sessions > 0) && (
        <TokenUsage
          usageStats={usageStats}
          currentInsight={currentInsight}
          onClearAllData={onClearAllData}
        />
      )}
      
      {!openaiApiKey && !aiInsights && (
        <div className="ai-setup-prompt">
          <p>🔑 Set up your OpenAI API key to get AI-powered task insights and recommendations.</p>
          <button
            onClick={onShowApiKeyModal}
            className="setup-ai-button"
          >
            Setup AI Analysis
          </button>
        </div>
      )}
    </div>
  );
};

export default AIInsights; 